// AtlasArcStageSelector.tsx - Atlas-specific stage selector using sphere.arcs[] multi-arc model

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import StageDropdownAtom from '../../../atoms/StageDropdown/StageDropdownAtom';
import TransitionCommentDialogMolecule from '../../TransitionCommentDialog/TransitionCommentDialogMolecule';
import { Sphere } from 'types/global';
import { ArcInfo } from 'types/arc';
import { useApiClientsOrNull } from '../../../../context/ApiClientsContext';
import { useTranslation } from 'localization';
import { PermissionList } from '../../../../../types/sharedTypes';
import { RootState, AppDispatch } from '@center-pwa/store/store';
import {
  fetchArcTemplate,
  selectArcTemplate,
  selectArcTemplateLoading,
  selectArcTemplateError,
} from '@center-pwa/reducers/arcTemplatesReducer';
import './AtlasArcStageSelectorMolecule.scss';

interface StageInstance {
  sphereId: string;
  stageInstanceId: string;
  stageDefinitionId: string;
  arcId: string;
  status: 'active' | 'not_started' | 'completed';
  startedAt: string | null;
  completedAt: string | null;
}

interface AtlasArcStageSelectorProps {
  sphere: Sphere;
  fetchPermissionList: (
    sphereId: string,
    nodeLabel: string,
    permissions?: string[]
  ) => Promise<PermissionList | null>;
  onStageUpdated: (sphereId: string, propertyName: string, updatedArc: Sphere) => Promise<void>;
  className?: string;
  style?: React.CSSProperties;
}

const AtlasArcStageSelectorMolecule: React.FC<AtlasArcStageSelectorProps> = ({
  sphere,
  fetchPermissionList,
  onStageUpdated,
  className = '',
  style = {},
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const apiClients = useApiClientsOrNull();
  const { t } = useTranslation();

  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [commentDialogVisible, setCommentDialogVisible] = useState(false);
  const [pendingTransitionTarget, setPendingTransitionTarget] = useState<string | null>(null);
  const [transitionLoading, setTransitionLoading] = useState(false);
  const [transitionError, setTransitionError] = useState<string | null>(null);

  // Read primary arc from sphere.arcs[] (multi-arc model)
  const primaryArc: ArcInfo | undefined = useMemo(() => {
    const arcs = sphere.arcs;
    if (!arcs || arcs.length === 0) return undefined;
    return arcs.find(a => a.isPrimary) ?? arcs[0];
  }, [sphere.arcs]);

  const hasValidArc = !!(primaryArc?.templateSystemName);
  const arcId = primaryArc?.id ?? '';
  const templateName = hasValidArc ? primaryArc.templateSystemName : '';

  // Map ArcInfo stages to StageInstance format for StageDropdownAtom
  const stageInstances = useMemo(() => {
    if (!primaryArc?.stages) return [];
    return primaryArc.stages.map((s): StageInstance => ({
      sphereId: '',
      stageInstanceId: '',
      stageDefinitionId: s.stageId,
      arcId: primaryArc.id,
      status: s.status,
      startedAt: s.startedAt,
      completedAt: s.completedAt,
    }));
  }, [primaryArc?.stages, primaryArc?.id]);

  // Get arc template from Redux store
  const arcTemplate = useSelector((state: RootState) =>
    hasValidArc ? selectArcTemplate(state, templateName) : null
  );
  const loading = useSelector((state: RootState) =>
    hasValidArc ? selectArcTemplateLoading(state, templateName) : false
  );
  const error = useSelector((state: RootState) =>
    hasValidArc ? selectArcTemplateError(state, templateName) : null
  );

  const currentStageId = primaryArc?.currentStageId ?? '';

  const currentStageDef = useMemo(() => {
    if (!arcTemplate || !currentStageId) return null;
    return arcTemplate.definition.stages.find(s => s.id === currentStageId);
  }, [arcTemplate, currentStageId]);

  const currentStageName = useMemo(() => {
    if (!currentStageDef) return t('arc.ui.no_active_stage');
    return t(currentStageDef.localizationKey, currentStageDef.fallbackName);
  }, [currentStageDef, t]);

  const pendingTransitionStage = useMemo(() => {
    if (!pendingTransitionTarget || !arcTemplate) return null;
    return arcTemplate?.definition.stages.find(s => s.id === pendingTransitionTarget);
  }, [pendingTransitionTarget, arcTemplate]);

  const requireComment = useMemo(() => {
    const stageDef = arcTemplate?.definition.stages.find(s => s.id === currentStageId);
    return stageDef?.transition?.requireComment ?? false;
  }, [arcTemplate, currentStageId]);

  // Fetch arc template from Redux (will use cache if available)
  useEffect(() => {
    if (!apiClients || !hasValidArc) return;
    const { schemaClient } = apiClients;

    if (!arcTemplate && !loading) {
      dispatch(fetchArcTemplate({ systemName: templateName, apiClient: schemaClient }));
    }
  }, [templateName, apiClients, arcTemplate, loading, dispatch, hasValidArc]);

  // Permission check callback (lazy, only called when user clicks)
  const permissionCheck = useCallback(async (): Promise<boolean> => {
    try {
      const permissionList = await fetchPermissionList(sphere.id, sphere.label || '', ['edit']);
      const canEdit = permissionList?.permissions?.edit || false;
      setHasPermission(canEdit);
      return canEdit;
    } catch {
      setHasPermission(false);
      return false;
    }
  }, [fetchPermissionList, sphere.id, sphere.label]);

  // Early returns AFTER all hooks
  if (!hasValidArc) {
    return (
      <div className={`atlas-arc-stage-selector-empty ${className}`} style={style}>
        <span className="empty-arc-placeholder">&nbsp;</span>
      </div>
    );
  }

  const handleTransition = async (newStageId: string, comment?: string) => {
    if (!apiClients) return;
    const { graphClient } = apiClients;
    try {
      setTransitionLoading(true);
      setTransitionError(null);
      const response = await graphClient.patch(`/arcs/${arcId}/transition`, {
        newStageId,
        comment,
      });

      if (response.status === 'success') {
        onStageUpdated(sphere.id, 'arc', response.arc);

        setCommentDialogVisible(false);
        setPendingTransitionTarget(null);
      }
    } catch (error: unknown) {
      let errorDetail = t('common.error.unknown');
      if (error && typeof error === 'object' && 'response' in error && error.response && typeof error.response === 'object' && 'data' in error.response && error.response.data && typeof error.response.data === 'object' && 'details' in error.response.data) {
        errorDetail = String(error.response.data.details);
      } else if (error instanceof Error) {
        errorDetail = error.message;
      }
      setTransitionError(`${t('arc.errors.transition_failed')}: ${errorDetail}`);
    } finally {
      setTransitionLoading(false);
    }
  };

  const handleRequestComment = (targetStageId: string) => {
    setPendingTransitionTarget(targetStageId);
    setCommentDialogVisible(true);
  };

  const handleCommentConfirm = (comment?: string) => {
    if (pendingTransitionTarget) {
      handleTransition(pendingTransitionTarget, comment);
    }
  };

  const handleCommentCancel = () => {
    setCommentDialogVisible(false);
    setPendingTransitionTarget(null);
  };

  if (error || transitionError) {
    return <div className="arc-stage-selector-error">{error || transitionError}</div>;
  }

  if (!arcTemplate || !apiClients) {
    return null;
  }

  return (
    <>
      <div className={`atlas-arc-stage-selector ${className}`} style={style}>
        <StageDropdownAtom
          stages={arcTemplate.definition.stages}
          stageInstances={stageInstances}
          currentStageId={currentStageId}
          currentStageName={currentStageName}
          loading={loading || transitionLoading}
          disabled={hasPermission === false}
          requireComment={requireComment}
          onTransition={handleTransition}
          onRequestComment={handleRequestComment}
          permissionCheck={permissionCheck}
          showChevron={false}
        />
      </div>

      <TransitionCommentDialogMolecule
        visible={commentDialogVisible}
        targetStageName={pendingTransitionStage ? t(pendingTransitionStage.localizationKey, pendingTransitionStage.fallbackName) : undefined}
        required={requireComment}
        onConfirm={handleCommentConfirm}
        onCancel={handleCommentCancel}
      />
    </>
  );
};

export default AtlasArcStageSelectorMolecule;
